import React from 'react';
import { Shield, AlertTriangle, Eye, Lock, Server, Globe, ArrowRight, CheckCircle, Target, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const OnePagerPage = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-white text-[#111] print:bg-white" data-testid="one-pager-page">
      {/* Print-optimized single page */}
      <div className="max-w-[850px] mx-auto px-8 py-10 print:px-6 print:py-4">

        {/* Header */}
        <div className="flex items-center justify-between mb-8 pb-6 border-b-2 border-[#0066FF]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0066FF] flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#111] tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Agent Control Plane
              </h1>
              <p className="text-sm text-[#666] -mt-0.5">Enterprise Security for AI Agents & Non-Human Identities</p>
            </div>
          </div>
          <div className="text-right text-xs text-[#999] print:block hidden sm:block">
            agentcontrolplane.io<br/>
            pilots@agentcontrolplane.io
          </div>
        </div>

        {/* The Problem */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-[#FF3B30]" />
            <h2 className="text-lg font-bold text-[#111]">The Problem</h2>
          </div>
          <p className="text-sm text-[#444] leading-relaxed">
            Enterprises now run hundreds of AI agents, service accounts, bots, and automated workflows — 
            <strong className="text-[#111]"> none of them governed by traditional IAM</strong>. 
            These non-human identities hold credentials, access production systems, make autonomous decisions, 
            and can escalate privileges without detection. The average enterprise has <strong>247+ non-human identities</strong>, 
            68% with excessive privileges, and fewer than 12% actively governed.
          </p>
        </div>

        {/* What ACP Does */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-5 h-5 text-[#0066FF]" />
            <h2 className="text-lg font-bold text-[#111]">What Agent Control Plane Does</h2>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: Eye, title: 'Discover', desc: 'Auto-discover every non-human identity across IdPs, cloud providers, and agent frameworks.' },
              { icon: Lock, title: 'Govern', desc: 'Define and enforce RBAC/ABAC policies. Detect violations continuously in real-time.' },
              { icon: Zap, title: 'Contain', desc: 'Instant kill switch: disable agents, revoke credentials, block tools. One click, fully audited.' },
              { icon: Shield, title: 'Audit', desc: 'Immutable audit trail of every action. Compliance-ready. Board-ready. Exportable.' },
            ].map((item, i) => (
              <div key={i} className="border border-[#E5E5E5] p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <item.icon className="w-4 h-4 text-[#0066FF]" />
                  <h3 className="text-sm font-bold text-[#111]">{item.title}</h3>
                </div>
                <p className="text-xs text-[#666] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Why Existing IAM Is Not Enough */}
        <div className="mb-8 bg-[#FAFAFA] border border-[#E5E5E5] p-5">
          <h2 className="text-sm font-bold text-[#111] uppercase tracking-wider mb-3">Why Existing IAM Is Not Enough</h2>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
            {[
              { gap: 'AI agents with autonomous decision-making', iam: 'IAM assumes human-initiated access' },
              { gap: 'Service accounts with standing privileges', iam: 'No continuous privilege review for NHIs' },
              { gap: 'Cross-environment credential sharing', iam: 'Siloed identity management per cloud' },
              { gap: 'No blast radius visibility', iam: 'No agent-to-resource dependency mapping' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2 py-1">
                <span className="text-[#FF3B30] font-bold mt-0.5 shrink-0">x</span>
                <div>
                  <span className="text-[#111] font-medium">{item.gap}</span>
                  <span className="text-[#999] ml-1">— {item.iam}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Deployment + Pilot side by side */}
        <div className="grid grid-cols-2 gap-6 mb-8">
          {/* Deployment Modes */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Server className="w-5 h-5 text-[#0066FF]" />
              <h2 className="text-lg font-bold text-[#111]">Deployment Modes</h2>
            </div>
            <div className="space-y-2">
              {[
                { mode: 'SaaS', desc: 'Managed infrastructure, read-only connectors, data encrypted at rest.' },
                { mode: 'Customer VPC', desc: 'Full deployment in your cloud. Data never leaves your perimeter.' },
                { mode: 'Air-Gapped', desc: 'Complete isolation. No external connections. Offline updates.' },
              ].map((item, i) => (
                <div key={i} className="border-l-2 border-[#0066FF] pl-3">
                  <h3 className="text-sm font-bold text-[#111]">{item.mode}</h3>
                  <p className="text-xs text-[#666]">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Controlled Pilot */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Globe className="w-5 h-5 text-[#34C759]" />
              <h2 className="text-lg font-bold text-[#111]">Controlled Pilot Approach</h2>
            </div>
            <div className="space-y-2">
              {[
                { step: 'Week 1', action: 'Deploy read-only connectors. Run first discovery scan. Map identity landscape.' },
                { step: 'Week 2', action: 'Activate policy engine. Review violations. Demonstrate kill switch and audit trail.' },
                { step: 'Review', action: 'Present findings to security leadership. Define rollout plan.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-16 shrink-0">
                    <span className="text-xs font-mono font-bold text-[#0066FF] bg-[#0066FF]/10 px-2 py-0.5">{item.step}</span>
                  </div>
                  <p className="text-xs text-[#666]">{item.action}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="border-t-2 border-[#0066FF] pt-5 flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-[#111]">Ready to see what's running in your environment?</p>
            <p className="text-xs text-[#666]">2-week pilot. Read-only deployment. Dedicated engineering support.</p>
          </div>
          <div className="flex items-center gap-3 print:hidden">
            <Link to="/request-pilot">
              <Button className="bg-[#0066FF] hover:bg-[#0052CC] text-white text-sm px-5 py-2" data-testid="onepager-cta-btn">
                Request a Pilot
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
          <div className="hidden print:block text-right">
            <p className="text-xs font-mono text-[#0066FF]">agentcontrolplane.io/request-pilot</p>
            <p className="text-xs text-[#999]">pilots@agentcontrolplane.io</p>
          </div>
        </div>

        {/* Print footer */}
        <div className="mt-6 pt-3 border-t border-[#E5E5E5] text-center print:block hidden">
          <p className="text-[10px] text-[#999]">
            &copy; 2026 Agent Control Plane. Confidential.
          </p>
        </div>
      </div>

      {/* Screen-only: back + print buttons */}
      <div className="max-w-[850px] mx-auto px-8 pb-10 flex items-center justify-between print:hidden">
        <Link to="/" className="text-sm text-[#999] hover:text-[#111] transition-colors">
          &larr; Back to site
        </Link>
        <button
          onClick={() => window.print()}
          className="text-sm text-[#0066FF] hover:text-[#0052CC] font-medium transition-colors"
          data-testid="print-btn"
        >
          Print / Save as PDF
        </button>
      </div>
    </div>
  );
};

export default OnePagerPage;
